import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';

import { userSelectors } from 'reducers';
import {
    useCreateDirectDebitMutation,
    useExportPaymentsMutation,
    useGetInvestmentFundsQuery,
    useGetPaymentsQuery,
    useUpdatePaymentMutation,
} from 'api/main';
import { addBusinessDays, parseFullName, parsePaymentNumber, parseSum } from 'utils/helpers';
import {
    listingActionButtonStyle,
    ORDER_BY,
    paymentEnrichments,
    paymentMethods,
    paymentStatus,
    roleCategories,
    RoleNames,
    sortDirection,
} from 'utils/constants';
import {
    useDebounce,
    useMatchAtLeastOneRole,
    useMyPromoter,
    useUserRoleMatchCategory,
} from 'utils/hooks';
import { texts } from 'utils/languages/fr';

import TotalRow from './TotalRow';
import DebitsPreviewDialog from './DebitsPreviewDialog';
import PaymentFilters from './PaymentFilters.js';
import {
    CustomButton,
    DataTile,
    EmptyTableBody,
    ErrorMessage,
    Loader,
    NativeDatePicker,
    StyledTableRow,
    CustomTablePagination,
    ActionsMenu,
    StyledTableCell,
    QuickSearchField,
    FiltersButton,
    CustomSelect,
    ActionButton,
} from 'components';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Box,
    IconButton,
    Checkbox,
    TableFooter,
    MenuItem,
    ListItemText,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as icons from 'mdi-material-ui';

const useStyles = makeStyles(theme => ({
    tile: {
        minHeight: 160,
        backgroundColor: theme.palette.grey[100],
        padding: '10px 15px',
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        flexDirection: 'column',
    },
    totalLabel: {
        fontWeight: 'bold',
    },
    total: {
        fontSize: '16px',
    },
    totalsGrid: {
        width: '30%',
        marginRight: theme.spacing(10),
    },
    executeButton: {
        alignSelf: 'center',
    },
    input: {
        margin: 0,
        padding: 0,
        height: '100%',
        width: '100%',
    },
    pickerButton: { margin: 0 },
    redCell: {
        color: theme.palette.error.main,
    },
    actionIcons: {
        fontSize: 25,
        color: 'inherit',
        marginRight: 10,
    },
}));

const ITEMS_PER_PAGE = 10;
const today = moment().startOf('day');

export const SettleMenuItem = ({ classes, loading, ...rest }) => (
    <MenuItem disabled={loading} classes={{ root: classes.menuItem }} {...rest}>
        <ListItemText disableTypography classes={{ root: classes.itemText }}>
            <Loader loading={loading} marginTop={0}>
                <icons.CashCheck className={classes.actionIcons} /> {texts.SETTLE_PAYMENT}
            </Loader>
        </ListItemText>
    </MenuItem>
);

export const ResetMenuItem = ({ classes, loading, ...rest }) => (
    <MenuItem disabled={loading} classes={{ root: classes.menuItem }} {...rest}>
        <ListItemText disableTypography classes={{ root: classes.itemText }}>
            <Loader loading={loading} marginTop={0}>
                <icons.ArrowUpLeftBold className={classes.actionIcons} /> {texts.RESET_PAYMENT}
            </Loader>
        </ListItemText>
    </MenuItem>
);

export const MoveMenuItem = ({ classes, loading, ...rest }) => (
    <MenuItem disabled={loading} classes={{ root: classes.menuItem }} {...rest}>
        <ListItemText disableTypography classes={{ root: classes.itemText }}>
            <Loader loading={loading} marginTop={0}>
                <icons.CashRefund className={classes.actionIcons} /> {texts.MOVE_PAYMENT}
            </Loader>
        </ListItemText>
    </MenuItem>
);

const FuturePaymentDate = ({ initialDate, paymentId, classes, isExecutable }) => {
    const [date, setDate] = useState(initialDate);

    const [updatePayment, { isLoading: loading }] = useUpdatePaymentMutation();

    const handleChangeDate = useCallback(newDate => {
        setDate(newDate);
    }, []);

    const handleSaveDate = useCallback(() => {
        updatePayment({ id: paymentId, transferDate: date.toISOString() });
    }, [paymentId, date, updatePayment]);

    if (initialDate?.isAfter(today, 'day') || !isExecutable) {
        return date?.format('L') || '-';
    }

    return (
        <Box display="flex" justifyContent="space-between" alignItems="center" height="100%">
            <NativeDatePicker
                className={classes.input}
                classes={{ input: classes.input }}
                shouldDisableDate={day => day.day() === 0 || day.day() === 6}
                disableFuture={false}
                handleDateChange={handleChangeDate}
                minDate={addBusinessDays(moment(), 2)}
                value={date}
                InputAdornmentProps={{ classes: { positionEnd: classes.pickerButton } }}
            />
            <Loader loading={loading} mt={0} size={20}>
                <IconButton
                    disableRipple
                    size="small"
                    color="primary"
                    disabled={initialDate ? initialDate.isSame(date, 'day') : null === date}
                    onClick={handleSaveDate}
                >
                    <icons.ContentSave />
                </IconButton>
            </Loader>
        </Box>
    );
};

const PaymentRow = props => {
    const { selectedPayments, classes, handleSelectPayment, payment, isServicer } = props;
    const {
        0: {
            amount,
            fundraisingNumber,
            fluxType,
            id,
            investedAmount,
            status,
            subscription: {
                investmentCharacteristics: {
                    shareSubcategory: { immediatePayment },
                },
                finishedSubscriptionAt,
                investmentFund,
            },
            transferDate,
            validatedBy,
        },
        recipientName,
        closingComment,
    } = payment;
    const fundName = investmentFund.name;
    const [updatePayment, { isSuccess: updateSuccess }] = useUpdatePaymentMutation();

    const createdDate = useMemo(
        () => (finishedSubscriptionAt ? moment(finishedSubscriptionAt) : null),
        [finishedSubscriptionAt]
    );
    const paymentTransferDate = useMemo(
        () => (transferDate ? moment.utc(transferDate) : null),
        [transferDate]
    );
    const hasFirstInstallment = useMemo(
        () => !!investmentFund?.firstInstalmentPercentage,
        [investmentFund]
    );
    const [menuOpen, setMenuOpen] = useState(false);
    const [ongoingAction, setOngoingAction] = useState('');
    const isDisabled = useMemo(
        () =>
            paymentTransferDate.isSameOrBefore(today, 'day') ||
            (selectedPayments?.[0] &&
                !paymentTransferDate.isSame(selectedPayments?.[0]?.paymentTransferDate, 'day')),
        [selectedPayments, paymentTransferDate]
    );
    const paymentNumberText =
        investmentFund.firstInstalmentPercentage === 100 && immediatePayment
            ? texts.FULL_CALL
            : parsePaymentNumber(
                  fluxType,
                  !!investmentFund.firstInstalmentPercentage
                      ? fundraisingNumber + 1
                      : fundraisingNumber,
                  closingComment
              );
    const isCritical = today.isAfter(paymentTransferDate, 'day');
    const rowClass =
        isCritical && status === paymentStatus.PENDING_CONFIRMATION ? classes.redCell : '';

    const handleUpdateStatus = useCallback(
        (paymentId, newStatus) => () => {
            setOngoingAction(newStatus);
            updatePayment({
                id: paymentId,
                status: newStatus,
            });
        },
        [updatePayment]
    );

    useEffect(() => {
        if (updateSuccess) {
            setMenuOpen(false);
            setOngoingAction('');
        }
    }, [updateSuccess]);

    const SettleItem = React.forwardRef((pr, ref) => <SettleMenuItem ref={ref} {...pr} />);
    const ResetItem = React.forwardRef((pr, ref) => <ResetMenuItem ref={ref} {...pr} />);
    const MoveItem = React.forwardRef((pr, ref) => <MoveMenuItem ref={ref} {...pr} />);

    return (
        <StyledTableRow key={id} classes={{ root: rowClass }}>
            {status === paymentStatus.CREATED && isServicer && (
                <StyledTableCell padding="checkbox">
                    <Checkbox
                        disabled={isDisabled}
                        disableRipple
                        checked={!!selectedPayments.find(p => p.id === id)}
                        onChange={handleSelectPayment?.({
                            id,
                            amount,
                            recipientName,
                            paymentTransferDate,
                            fundName,
                        })}
                    />
                </StyledTableCell>
            )}
            <StyledTableCell>{recipientName}</StyledTableCell>
            <StyledTableCell>{createdDate?.format('L') || ''}</StyledTableCell>
            <StyledTableCell>{investmentFund.type || ''}</StyledTableCell>
            <StyledTableCell>{fundName || ''}</StyledTableCell>
            <StyledTableCell>{paymentNumberText}</StyledTableCell>
            <StyledTableCell>{parseSum(investedAmount)}</StyledTableCell>
            <StyledTableCell>{parseSum(amount)}</StyledTableCell>
            <StyledTableCell>
                {isServicer ? (
                    <FuturePaymentDate
                        isExecutable={status === paymentStatus.CREATED}
                        paymentId={id}
                        initialDate={paymentTransferDate}
                        classes={classes}
                    />
                ) : (
                    paymentTransferDate.format('L')
                )}
            </StyledTableCell>
            {status === paymentStatus.PENDING_CONFIRMATION && isServicer && (
                <StyledTableCell>
                    <ActionsMenu
                        controlled
                        open={menuOpen}
                        handleOpen={(value = true) => setMenuOpen(value)}
                    >
                        <SettleItem
                            loading={ongoingAction === paymentStatus.CONFIRMED}
                            onClick={handleUpdateStatus(id, paymentStatus.CONFIRMED)}
                        />
                        <ResetItem
                            loading={ongoingAction === paymentStatus.FAILED}
                            onClick={handleUpdateStatus(id, paymentStatus.FAILED)}
                        />
                    </ActionsMenu>
                </StyledTableCell>
            )}
            {status === paymentStatus.CONFIRMED && (
                <StyledTableCell>{validatedBy ? parseFullName(validatedBy) : '-'}</StyledTableCell>
            )}
            {status === paymentStatus.CONFIRMED && isServicer && (
                <StyledTableCell>
                    <ActionsMenu
                        controlled
                        open={menuOpen}
                        handleOpen={(value = true) => setMenuOpen(value)}
                    >
                        <MoveItem
                            loading={ongoingAction === paymentStatus.CONFIRMED}
                            onClick={handleUpdateStatus(id, paymentStatus.PENDING_CONFIRMATION)}
                        />
                    </ActionsMenu>
                </StyledTableCell>
            )}
        </StyledTableRow>
    );
};

const DebitsTable = props => {
    const { listStatus, tableHeads, title, totalRowProps } = props;
    const classes = useStyles();

    const currentSubscription = useSelector(userSelectors.getCurrentSubscription);
    const internalUser = useSelector(userSelectors.getInternalUser);
    const isServicer = useUserRoleMatchCategory(roleCategories.SERVICERS);
    const isPromoter = useUserRoleMatchCategory(roleCategories.PROMOTERS);
    const isAdminCommercial = useMatchAtLeastOneRole([RoleNames.ADMIN_CIF, RoleNames.PSI]);
    const userIsStandardServicer = useMatchAtLeastOneRole([RoleNames.SERVICER_STANDARD]);

    const [previewOpen, setPreviewOpen] = useState(false);
    const [paymentsError, setPaymentsError] = useState('');
    const [selectedPayments, setSelectedPayments] = useState([]);
    const [filtersOpen, setFiltersOpen] = useState(false);

    const [filters, setFilters] = useState({
        page: 1,
        orderBy: ORDER_BY.finishedSubscriptionAt,
        direction: sortDirection.ASC,
        enrichments: Object.values(paymentEnrichments),
        type: paymentMethods.DIRECT_DEBIT,
        itemsPerPage: ITEMS_PER_PAGE,
        status: listStatus,
        company: isAdminCommercial ? currentSubscription?.distributorCompany?.id : undefined,
        product: [],
        roles: [],
    });
    const [keyword, setKeyword] = useState('');
    const debouncedKeyword = useDebounce(keyword, 500);
    const { promoter } = useMyPromoter();

    const { data: fundList = [], isFetching: fundsLoader } = useGetInvestmentFundsQuery(
        {
            visible: 1,
            promoterId: isPromoter ? promoter?.id : undefined,
            distributorId: isServicer
                ? undefined
                : internalUser.subscriptions?.[0]?.distributorCompany?.id,
        },
        { skip: isPromoter && !promoter?.id }
    );

    const {
        data: { payments = [], lastPage } = { payments: [], lastPage: 1 },
        isFetching: listLoader,
    } = useGetPaymentsQuery(filters);
    const [
        createDirectDebits,
        {
            isLoading: createLoading,
            isSuccess: createSuccess,
            isError: createError,
            error: directDebitError,
        },
    ] = useCreateDirectDebitMutation();
    const [exportPayments, { isLoading: exportLoading }] = useExportPaymentsMutation();

    const handleToggleFilters = useCallback(() => setFiltersOpen(prevState => !prevState), []);
    const handleApplyFilters = useCallback(
        customFilters => {
            setFilters(prevState => ({ ...prevState, ...customFilters, page: 1 }));
        },
        [setFilters]
    );

    const handleChangePage = useCallback(
        page => () => setFilters(prevState => ({ ...prevState, page })),
        []
    );

    const isAdminStandard = useMemo(
        () => currentSubscription?.role?.name === RoleNames.SERVICER_STANDARD,
        [currentSubscription]
    );

    const totalPaymentAmount = useMemo(
        () => payments.reduce((acc, payment) => acc + payment[0].amount, 0),
        [payments]
    );

    const selectedPaymentsTotal = useMemo(
        () => selectedPayments.reduce((acc, payment) => acc + payment.amount, 0),
        [selectedPayments]
    );

    const filteredTableHeads = useMemo(() => {
        if (isServicer)
            return tableHeads.filter(th =>
                userIsStandardServicer ? th.id !== 'actions' && th.id !== 'checkbox' : true
            );
        if (
            listStatus === paymentStatus.CREATED ||
            listStatus === paymentStatus.CONFIRMED ||
            listStatus === paymentStatus.PENDING_CONFIRMATION
        ) {
            return tableHeads.filter(th => th.id !== 'checkbox' && th.id !== 'actions');
        }
        return tableHeads;
    }, [isServicer, tableHeads, listStatus, userIsStandardServicer]);

    const leftOutWidth = useMemo(
        () =>
            tableHeads
                .filter(th => !filteredTableHeads.find(fth => fth.id === th.id))
                .reduce((acc, crt) => acc + crt.width, 0),
        [tableHeads, filteredTableHeads]
    );

    const getTotalsList = useCallback(
        listType => {
            switch (listType) {
                case paymentStatus.CREATED:
                    return [totalPaymentAmount, selectedPaymentsTotal];
                case paymentStatus.PENDING_CONFIRMATION:
                    return [totalPaymentAmount];
                case paymentStatus.CONFIRMED:
                    return [];

                default:
                    return [totalPaymentAmount];
            }
        },
        [totalPaymentAmount, selectedPaymentsTotal]
    );

    const handleSelectPayment = useCallback(
        payment => () => {
            setSelectedPayments(prevState => {
                const found = prevState.find(p => p.id === payment.id);
                if (found) {
                    return prevState.filter(p => p.id !== payment.id);
                }
                return [...prevState, payment];
            });
        },
        []
    );

    const handleForwardPayments = useCallback(() => {
        const paymentIds = selectedPayments.map(p => p.id);
        const investmentFundId = +filters.product || null;
        if (paymentIds.length && investmentFundId) {
            createDirectDebits({ paymentIds, investmentFundId });
        }
    }, [createDirectDebits, selectedPayments, filters.product]);

    const handleOpenPreview = useCallback(() => setPreviewOpen(true), []);

    const handlePreviewClose = useCallback(() => {
        setSelectedPayments([]);
        setPreviewOpen(false);
    }, []);

    const handlePaymentsExport = useCallback(() => {
        exportPayments(filters);
    }, [filters, exportPayments]);

    useEffect(() => {
        if (createSuccess) {
            setSelectedPayments([]);
            setPreviewOpen(false);
        }
    }, [createSuccess]);

    useEffect(() => {
        if (createError && directDebitError?.detail === 'payment.exists') {
            setPaymentsError(texts.SEPA_ALREADY_GENERATED_TODAY);
        }
    }, [createError, directDebitError]);

    useEffect(() => {
        // reset page to 1 when selected keyword or product is changed (on the 'CREATED' status list)
        setFilters(prevState => ({
            ...prevState,
            keyword: debouncedKeyword,
            product:
                !prevState.product.length && fundList.length && listStatus === paymentStatus.CREATED
                    ? [fundList[0].id]
                    : prevState.product,
            page: 1,
        }));
    }, [debouncedKeyword, fundList, listStatus]);

    return (
        <DataTile className={classes.tile}>
            <DebitsPreviewDialog
                open={previewOpen}
                sepa={previewOpen}
                onClose={handlePreviewClose}
                payments={selectedPayments}
                onValidate={handleForwardPayments}
                loading={createLoading}
            />
            <Typography variant="h5" color="primary" gutterBottom>
                {title}
            </Typography>
            <Box display="flex" alignItems="center" justifyContent="space-between" padding={2}>
                <Box display="flex" alignItems="center" padding={2}>
                    {listStatus === paymentStatus.CREATED ? (
                        <Loader marginTop={1} loading={fundsLoader}>
                            <CustomSelect
                                value={filters.product?.[0] || ''}
                                label={texts.PRODUIT}
                                onChange={e =>
                                    setFilters(prevState => ({
                                        ...prevState,
                                        product: [+e.target.value],
                                    }))
                                }
                                controlSx={{ marginBottom: 0, width: '300px' }}
                            >
                                {fundList.map(fund => (
                                    <MenuItem key={fund.id} value={fund.id}>
                                        {fund.name}
                                    </MenuItem>
                                ))}
                            </CustomSelect>
                        </Loader>
                    ) : (
                        <React.Fragment>
                            <QuickSearchField
                                value={keyword}
                                onChange={e => setKeyword(e.target.value)}
                                handleClear={() => setKeyword('')}
                            />
                            <FiltersButton onClick={handleToggleFilters} />
                        </React.Fragment>
                    )}
                </Box>
                {(listStatus === paymentStatus.CONFIRMED ||
                    listStatus === paymentStatus.PENDING_CONFIRMATION) && (
                    <ActionButton
                        variant="outlined"
                        sx={listingActionButtonStyle}
                        disabled={exportLoading}
                        onClick={handlePaymentsExport}
                    >
                        {texts.EXPORTER_LISTE}
                    </ActionButton>
                )}
            </Box>
            {listStatus !== paymentStatus.CREATED && (
                <PaymentFilters
                    open={filtersOpen}
                    applyFilters={handleApplyFilters}
                    loading={listLoader}
                />
            )}
            <Loader loading={listLoader} height="100px" marginTop={0}>
                <TableContainer className={classes.tableContainer}>
                    <Table style={{ tableLayout: 'fixed' }}>
                        <colgroup>
                            {filteredTableHeads.map(th => {
                                let extraWidth = th.id === ORDER_BY.lastName ? leftOutWidth : 0;
                                return <col key={th.label} width={`${th.width + extraWidth}%`} />;
                            })}
                        </colgroup>
                        <TableHead>
                            <TableRow>
                                {filteredTableHeads.map(th => (
                                    <TableCell key={th.label}>{th.label}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        {payments.length === 0 ? (
                            <EmptyTableBody colSpan={filteredTableHeads.length} />
                        ) : (
                            <TableBody>
                                {payments.map(p => (
                                    <PaymentRow
                                        key={p[0].id}
                                        payment={p}
                                        isServicer={isServicer && !userIsStandardServicer}
                                        handleSelectPayment={handleSelectPayment}
                                        selectedPayments={selectedPayments}
                                        classes={classes}
                                    />
                                ))}
                                {totalRowProps && getTotalsList(listStatus) && (
                                    <TotalRow
                                        {...totalRowProps}
                                        totals={getTotalsList(listStatus)}
                                        buttonComponent={
                                            !isAdminStandard &&
                                            isServicer &&
                                            listStatus === paymentStatus.CREATED ? (
                                                <CustomButton
                                                    disabled={selectedPayments.length === 0}
                                                    label={texts.EXECUTE_PAYMENTS}
                                                    onClick={handleOpenPreview}
                                                    variant="contained"
                                                    className={classes.executeButton}
                                                    size="small"
                                                />
                                            ) : null
                                        }
                                    />
                                )}
                            </TableBody>
                        )}
                        <TableFooter>
                            <TableRow>
                                <CustomTablePagination
                                    currentPage={filters.page}
                                    lastPage={lastPage}
                                    changePage={handleChangePage}
                                    columns={filteredTableHeads.length}
                                />
                            </TableRow>
                        </TableFooter>
                    </Table>
                </TableContainer>
            </Loader>
            {paymentsError && <ErrorMessage align="center" gutterBottom message={paymentsError} />}
        </DataTile>
    );
};

export default DebitsTable;
